import { unified } from "unified"
import remarkParse from "remark-parse"
import remarkGfm from "remark-gfm"
import remarkMath from "remark-math"
import remarkDirective from "remark-directive"
import { visit } from "unist-util-visit"
import type { Content, Heading } from "mdast"
import slugify from "slugify"
import type {
  ResolvedBranchesData,
  ResolvedBranchNode,
  ResolvedBranchNodeSection,
} from "@/lib/branchTypes"

// Maps a URL hash (#<anchor>) to a place in the branch tree. The Playbook
// is a single-page app with no per-section routes, so a deep link needs
// (1) which branch node to expand and (2) which DOM element to scroll to.
export type AnchorKind = "node" | "section" | "heading"
export type AnchorTarget = {
  nodeId: string // node whose subtree must be expanded
  elementId: string // DOM id to scrollIntoView
  kind: AnchorKind
}

// Mirrors getTextFromNode in app/common/Markdown.tsx (children joined with
// a single space) so the slug we compute equals the id the renderer emits.
function getTextFromNode(node: Content): string {
  if (node.type === "text") return node.value
  if ("children" in node && Array.isArray(node.children)) {
    return (node.children as Content[]).map(getTextFromNode).join(" ")
  }
  return ""
}

// Same call as app/common/Markdown.tsx:313 — keep these in lockstep.
function headingSlug(text: string): string {
  return slugify(text, { lower: true, strict: true })
}

const headingProcessor = unified()
  .use(remarkParse)
  .use(remarkGfm)
  .use(remarkMath)
  .use(remarkDirective)

function collectHeadingSlugs(markdown: string): string[] {
  if (!markdown.trim()) return []
  const tree = headingProcessor.parse(markdown)
  const slugs: string[] = []
  visit(tree, "heading", (node: Heading) => {
    const text = node.children.map(c => getTextFromNode(c as Content)).join(" ")
    const slug = headingSlug(text)
    if (slug) slugs.push(slug)
  })
  return slugs
}

// Mirrors BranchpointBox.splitBranchpointChildren — finds the title heading
// inside a :::branchpoint (either nested in :::branchSummary or as a direct
// child) and returns its slug. Must match the id BranchpointBox emits.
function getBranchpointSlug(container: any): string | null {
  const children = (container?.children ?? []) as Content[]
  let titleNode: Content | undefined
  const first = children[0] as any
  if (first?.type === "containerDirective" && first.name === "branchSummary") {
    titleNode = (first.children ?? []).find((c: any) => c.type === "heading")
  } else {
    titleNode = children.find((c: any) => c.type === "heading")
  }
  if (!titleNode) return null
  const text = getTextFromNode(titleNode).trim()
  const slug = headingSlug(text)
  return slug ? `branchpoint-${slug}` : null
}

function collectBranchpointSlugs(markdown: string): string[] {
  if (!markdown.trim()) return []
  const tree = headingProcessor.parse(markdown)
  const slugs: string[] = []
  visit(tree, "containerDirective", (node: any) => {
    if (node.name !== "branchpoint") return
    const slug = getBranchpointSlug(node)
    if (slug) slugs.push(slug)
  })
  return slugs
}

// Mirrors lib/remarkDetails.ts: a `:::details` block's summary is the text of
// its first paragraph (soft line breaks joined with "\n"), and Markdown.tsx
// emits the `<details id>` as slugify(summary). Reproduce both exactly so a
// `#<details-slug>` deep link resolves to the branch node that owns it — which
// lets useAnchorDeepLink expand the right plan/branch and center the section,
// instead of the index missing it and falling back to a non-expanding scroll.
function getDetailsSummary(container: any): string {
  const first = (container?.children ?? [])[0]
  if (!first || first.type !== "paragraph") return ""
  return (first.children ?? [])
    .map((c: Content) => getTextFromNode(c))
    .join("")
    .split("\n")
    .map((line: string) => line.trim())
    .filter((line: string) => line.length > 0)
    .join("\n")
    .trim()
}

function collectDetailsSlugs(markdown: string): string[] {
  if (!markdown.trim()) return []
  const tree = headingProcessor.parse(markdown)
  const slugs: string[] = []
  visit(tree, "containerDirective", (node: any) => {
    if (node.name !== "details") return
    const summary = getDetailsSummary(node)
    const slug = summary ? headingSlug(summary) : ""
    if (slug) slugs.push(slug)
  })
  return slugs
}

// Precedence is enforced by insertion order + first-wins: node ids beat
// section ids beat heading slugs. Real collisions are unlikely (section
// ids are prefixed `plan-a--`, node ids are `plan-*-root`), but the order
// makes the outcome deterministic.
export function buildAnchorIndex(
  branchData: ResolvedBranchesData,
): Map<string, AnchorTarget> {
  const index = new Map<string, AnchorTarget>()
  const add = (key: string, target: AnchorTarget) => {
    if (key && !index.has(key)) index.set(key, target)
  }

  const nodes = Object.values(branchData.nodes) as ResolvedBranchNode[]

  for (const node of nodes) {
    const elementId = node.anchor || node.id
    add(node.id, { nodeId: node.id, elementId, kind: "node" })
    if (node.anchor) add(node.anchor, { nodeId: node.id, elementId, kind: "node" })
  }
  for (const node of nodes) {
    const sections = (node.sections ?? []) as ResolvedBranchNodeSection[]
    for (const section of sections) {
      add(section.id, { nodeId: node.id, elementId: section.id, kind: "section" })
    }
  }
  for (const node of nodes) {
    const sections = (node.sections ?? []) as ResolvedBranchNodeSection[]
    const markdownBlocks = [
      node.content?.markdown ?? "",
      ...sections.map(s => s.content?.markdown ?? ""),
    ]
    for (const md of markdownBlocks) {
      for (const slug of collectHeadingSlugs(md)) {
        add(slug, { nodeId: node.id, elementId: slug, kind: "heading" })
      }
      for (const slug of collectBranchpointSlugs(md)) {
        add(slug, { nodeId: node.id, elementId: slug, kind: "heading" })
      }
      for (const slug of collectDetailsSlugs(md)) {
        add(slug, { nodeId: node.id, elementId: slug, kind: "heading" })
      }
    }
  }

  return index
}

// SSR-safe (pure). Unknown anchors return null so the caller can no-op
// without breaking the page.
export function resolveAnchor(
  rawHash: string,
  index: Map<string, AnchorTarget>,
  branchData: ResolvedBranchesData,
): AnchorTarget | null {
  let key = rawHash.startsWith("#") ? rawHash.slice(1) : rawHash
  try {
    key = decodeURIComponent(key)
  } catch {
    // keep raw key if it isn't valid percent-encoding
  }
  key = key.trim()
  if (!key) return null

  const hit = index.get(key)
  if (hit) return hit

  // Defensive: a node that somehow wasn't pre-registered.
  if (branchData.nodes[key]) {
    return { nodeId: key, elementId: key, kind: "node" }
  }
  return null
}
